import announcementView from './announcementComponent.html';
import './announcementComponent.css';
import { overlay } from './overlay.js';
import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { zIndexManager } from 'components/sharpr/zIndex/manager/zIndexManager';

export function announcementComponent() {
	return {
		bindings: {
			record: '<',
		},
		controllerAs: 'vm',
		controller: announcementComponentCtrl,
		template: announcementView,
	};
}

export class announcementComponentCtrl {
	constructor(Announcement, Identity, $timeout) {
		'ngInject';
		// save injections
		this.Announcement = Announcement;
		this.Identity = Identity;
		this.$timeout = $timeout;
		// starting variables
		this.overlayReady = false;
		this.close = this.close.bind(this);
		this.closeIfEsc = this.closeIfEsc.bind(this);
		this.closeIfContent = this.closeIfContent.bind(this);
	}

	// runs when component is in the DOM and bindings are set
	$onInit() {
		// hide the scrollbar
		this.oldOverflow = document.documentElement.style.overflow;
		document.documentElement.style.overflow = 'hidden';
		// insert CSS if applicable
		if (this.record.css) {
			this.style = AssetManager.style(this.record.css);
		}
		// wait until children are rendered, then set up overlays
		this.$timeout(() => {
			// get all items to overlay
			const items = [...document.querySelectorAll('.overlay-item')];
			// setup the overlayer
			this.overlay = overlay(items);
			// position all the overlaid elements
			this.overlay.setup();
			// tell centered content (if any) to fade in
			this.overlayReady = true;
			// mark all overlay items as ready so they will fade in
			// and make sure overlaid elements will appear on the top
			setTimeout(() => {
				const access = this.getUserAccess();
				[...document.querySelectorAll('[data-overlay-criteria]')].forEach(node => {
					const criteriaString = node.getAttribute('data-overlay-criteria');
					if (!this.passesCriteria(access, criteriaString)) {
						node.parentNode.removeChild(node);
					}
				});
				[...document.querySelectorAll('[data-hub-links]')].forEach(node => {
					const hubIds = node.getAttribute('data-hub-links').split(/\s*,\s*/);
					const heading = node.textContent.trim();
					node.innerHTML = this.getHubLinksHtml(heading, hubIds);
				});
				// hide overlay items if the user doesn't have the requested permission
				[...document.querySelectorAll('[data-overlay-permission]')].forEach(node => {
					const perm = node.getAttribute('data-overlay-permission');
					if (!this.Identity.can(perm)) {
						node.parentNode.removeChild(node);
					}
				});
				items.forEach(item => {
					if (!item.parentNode) {
						return;
					}
					zIndexManager.bringToTop(item);
					item.classList.add('ready');
				});
			}, 50);
			setTimeout(() => {
				this.overlay.setup();
				// close when .overlay-close elements are clicked (and move them to top)
				[...document.querySelectorAll('.overlay-close')].forEach(close => {
					close.addEventListener('click', this.close);
					close.style.zIndex = zIndexManager.next();
				});
				// close when background is clicked or on ESC key
				window.addEventListener('keyup', this.closeIfEsc);
				document.querySelector('.announcement-content').addEventListener('click', this.closeIfContent);
			}, 1500);
		}, 50);
	}

	getUserAccess() {
		const hubIds = {};
		this.Identity.allowedClientIds().forEach(id => {
			hubIds[`hub_${id}`] = true;
		});
		return {
			hasMultipleHubs: this.Identity.hasMultipleHubs(),
			role: this.Identity.role(),
			hubId: this.Identity.currentClient().id,
			accountId: this.Identity.currentAffiliationId(),
			isAccountManager: this.Identity.isAccountManager(),
			...hubIds,
		};
	}

	getHubLinksHtml(heading, hubIds) {
		const hubNamesById = {};
		this.Identity.hubChoices().forEach(hub => {
			hubNamesById[hub.id] = hub.$label;
		});
		const toShow = hubIds.filter(id => hubNamesById[id]);
		if (toShow.length === 0) {
			return '';
		}
		/* prettier-ignore */
		return `
			<div class="hub-links-label">${heading}</div>
			<div class="hub-links">
				${toShow.map(id => `
					<div>
						<a
							class="hub-link"
							href="/users/setclient/${id}?backTo=%2Fhub%23%2Fposts"
						>
							${hubNamesById[id]}
						</a>
					</div>
				`).join('')}
			</div>
		`;
	}

	/*
Examples
<div data-overlay-criteria='{"hasMultipleHubs":true}'>
    Only show me if user has multiple hubs
</div>
<div data-overlay-criteria='{"can_send_email":true}'>
    Only show me if user is allowed to send email
</div>
<div data-overlay-criteria='{"hubId":123}'>
    Only show me if current hub id is 123
</div>
<div data-overlay-criteria='{"hub_456":true}'>
    Only show me if I can access hub 456
</div>
*/
	/**
	 * Check if the given criteria object matches the criteria attribute string
	 * @param {Object} access  User access details from the getUserAccess()
	 * @param {String} criteriaString  String in the node's data-dest-criteria attribute
	 * @returns {boolean}
	 */
	passesCriteria(access, criteriaString) {
		try {
			const mustMatch = JSON.parse(criteriaString);
			for (const prop in mustMatch) {
				if (!mustMatch.hasOwnProperty(prop)) {
					continue;
				}
				// allow loose comparison by design
				if (access[prop] == mustMatch[prop]) {
					return false;
				}
			}
			return true;
		} catch (e) {
			console.error('data-overlay-criteria attribute is invalid JSON.', { criteriaString });
			return true;
		}
	}

	close() {
		// this will remove the element from the DOM and trigger $onDestroy
		this.Announcement.closePopup();
	}

	closeIfEsc(evt) {
		if (evt.which === 27) {
			// escape key
			this.close();
		}
	}

	closeIfContent(evt) {
		if (evt.target.classList.contains('announcement-content')) {
			// clicked on the outer content area, not any children
			this.close();
		}
	}

	$onDestroy() {
		// restore the page scrolling to its former glory
		document.documentElement.style.overflow = this.oldOverflow;
		// make sure user doesn't see this announcement again
		this.Announcement.markAsSeen(this.record);
		// remove any styling
		AssetManager.removeElement(this.style);
		// remove overlays
		this.overlay.teardown();
		// stop listening for ESC key
		window.removeEventListener('keyup', this.closeIfEsc);
	}
}
